/**
 * MASSER UI — Design Tokens (Oficial)
 * Fuente: masser-tokens.css / masser-design-system.json
 * Figma: EDheLlft1iMvqSGQKwolJq  |  Exportado: 2026-04-16
 */

// ─── COLORES RAW ─────────────────────────────────────────────────────────────
export const RAW = {
  // Brand — Rojo Terpel
  brandPrimary:        '#DF1A09',
  brandPrimaryHover:   '#EC000F',
  brandPrimaryPressed: '#B50000',
  brandPrimaryDark:    '#A6040E',
  brandPrimaryLight:   '#FF5F5F',
  brandPrimarySubtle:  '#FFBDBD',
  brandErrorAlt:       '#F23E48',
  brandErrorPink:      '#FF7186',

  // Combustibles
  fuelYellow:          '#FFDE4E',
  fuelAmber:           '#FFA600',
  fuelAmberLight:      '#FFBF00',
  fuelOrange:          '#FF9600',
  fuelText:            '#664A00',

  // Éxito
  successDefault:      '#0FAB0B',
  successDark:         '#086006',
  successBg:           '#E9F6F0',

  // Información
  infoDefault:         '#0070D5',
  infoDark:            '#0043B3',
  infoBg:              '#E3E7FC',

  // Error
  errorDefault:        '#B50000',
  errorLight:          '#FF5F5F',
  errorSubtle:         '#FFBDBD',

  // Warning
  warningDefault:      '#FFBF00',
  warningDark:         '#FFA600',

  // Neutros oscuros
  navy900:             '#001333',
  navy800:             '#141A38',
  navy700:             '#41416B',
  gray700:             '#454455',
  gray600:             '#4B4A5C',
  slate600:            '#42535C',

  // Neutros medios
  slate400:            '#7D8F99',
  gray400:             '#888A97',
  slate200:            '#B8C5CC',
  gray200:             '#C7C8CD',
  gray100:             '#DADADA',

  // Neutros claros
  gray50:              '#E3E6E6',
  slate50:             '#DAE2E5',
  purple50:            '#F0EFF5',
  neutral30:           '#F4F5F5',
  neutral20:           '#F7F9FA',
  white98:             '#FEFFFF',
  white:               '#FFFFFF',
} as const;

// ─── SEMÁNTICOS ──────────────────────────────────────────────────────────────
export const colors = {
  // Marca
  brand:               RAW.brandPrimary,
  brandHover:          RAW.brandPrimaryHover,
  brandPressed:        RAW.brandPrimaryPressed,
  brandLight:          RAW.brandPrimaryLight,
  brandSubtle:         RAW.brandPrimarySubtle,

  // Estado
  success:             RAW.successDefault,
  successDark:         RAW.successDark,
  successBg:           RAW.successBg,
  info:                RAW.infoDefault,
  infoDark:            RAW.infoDark,
  infoBg:              RAW.infoBg,
  error:               RAW.errorDefault,
  errorLight:          RAW.errorLight,
  errorSubtle:         RAW.errorSubtle,
  warning:             RAW.warningDefault,
  warningDark:         RAW.warningDark,
  warningBg:           '#FFFBE6',
  warningText:         RAW.fuelText,

  // Texto (aliases semánticos → CSS variables)
  textPrimary:         RAW.gray600,       // #4B4A5C
  textHeading:         RAW.navy900,       // #001333
  textSecondary:       RAW.slate400,      // #7D8F99
  textDisabled:        RAW.slate200,      // #B8C5CC
  textOnBrand:         RAW.white,         // #FFFFFF
  textOnDark:          RAW.white98,       // #FEFFFF

  // Fondos
  bgPage:              RAW.neutral20,     // #F7F9FA
  bgSurface:           RAW.white,         // #FFFFFF
  bgAlt:               RAW.neutral30,     // #F4F5F5
  bgNavDark:           RAW.navy800,       // #141A38
  bgNavDarkHover:      '#1E2650',

  // Bordes
  borderDefault:       RAW.gray100,       // #DADADA
  borderInput:         RAW.gray200,       // #C7C8CD
  borderStrong:        RAW.slate200,      // #B8C5CC
  borderSoft:          RAW.gray50,        // #E3E6E6
} as const;

// ─── TIPOGRAFÍA ──────────────────────────────────────────────────────────────
export const typography = {
  fontBrand:   '"Terpel Sans", sans-serif',
  fontBody:    '"Nunito", sans-serif',
  fontSystem:  '"Terpel Sans", "Nunito", system-ui, -apple-system, sans-serif',

  sizeMicro:   '10px',
  sizeXs:      '12px',
  sizeSm:      '14px',
  sizeMd:      '16px',
  sizeLg:      '18px',
  sizeXl:      '20px',
  size2xl:     '24px',
  size3xl:     '26px',

  weightLight:     300,
  weightRegular:   400,
  weightMedium:    500,
  weightSemibold:  600,
  weightBold:      700,
  weightExtrabold: 800,
  weightBlack:     900,
} as const;

// ─── ESPACIADO ───────────────────────────────────────────────────────────────
export const spacing = {
  '1': '4px',   '2': '8px',  '3': '12px', '4': '16px',
  '5': '20px',  '6': '24px', '8': '32px', '10': '40px',
  '12': '48px', '16': '64px',
} as const;

// ─── RADIOS ──────────────────────────────────────────────────────────────────
export const radius = {
  xs: '4px', sm: '8px', md: '12px', lg: '16px',
  xl: '20px', xxl: '24px', pill: '40px', full: '9999px',
} as const;

// ─── SOMBRAS ─────────────────────────────────────────────────────────────────
export const shadows = {
  sm:  '0 1px 3px rgba(0,19,51,0.08)',
  md:  '0 2px 8px rgba(0,19,51,0.12)',
  lg:  '0 4px 16px rgba(0,19,51,0.16)',
  xl:  '0 8px 32px rgba(0,19,51,0.20)',
} as const;

// ─── CSS VARIABLES (inyectar en :root) ───────────────────────────────────────
export const MASSER_CSS_VARS = `
:root {
  /* Brand — Rojo Terpel */
  --color-brand:               #DF1A09;
  --color-brand-hover:         #EC000F;
  --color-brand-pressed:       #B50000;
  --color-brand-light:         #FF5F5F;
  --color-brand-subtle:        #FFBDBD;

  /* Estado */
  --color-success:             #0FAB0B;
  --color-success-dark:        #086006;
  --color-success-bg:          #E9F6F0;
  --color-info:                #0070D5;
  --color-info-dark:           #0043B3;
  --color-info-bg:             #E3E7FC;
  --color-error:               #B50000;
  --color-error-light:         #FF5F5F;
  --color-error-subtle:        #FFBDBD;
  --color-warning:             #FFBF00;
  --color-warning-dark:        #FFA600;
  --color-warning-bg:          #FFFBE6;
  --color-warning-text:        #664A00;

  /* Neutros oscuros */
  --color-navy-900:            #001333;
  --color-navy-800:            #141A38;
  --color-navy-700:            #41416B;
  --color-gray-700:            #454455;
  --color-gray-600:            #4B4A5C;
  --color-slate-600:           #42535C;

  /* Neutros medios */
  --color-slate-400:           #7D8F99;
  --color-gray-400:            #888A97;
  --color-slate-200:           #B8C5CC;
  --color-gray-200:            #C7C8CD;
  --color-gray-100:            #DADADA;

  /* Neutros claros */
  --color-gray-50:             #E3E6E6;
  --color-purple-50:           #F0EFF5;
  --color-neutral-30:          #F4F5F5;
  --color-neutral-20:          #F7F9FA;
  --color-white-98:            #FEFFFF;
  --color-white:               #FFFFFF;

  /* Semánticos */
  --color-text-primary:        #4B4A5C;
  --color-text-heading:        #001333;
  --color-text-secondary:      #7D8F99;
  --color-text-disabled:       #B8C5CC;
  --color-text-on-brand:       #FFFFFF;
  --color-text-on-dark:        #FEFFFF;
  --color-bg-page:             #F7F9FA;
  --color-bg-surface:          #FFFFFF;
  --color-bg-alt:              #F4F5F5;
  --color-bg-nav:              #141A38;
  --color-bg-nav-hover:        #1E2650;
  --color-border-default:      #DADADA;
  --color-border-input:        #C7C8CD;
  --color-border-strong:       #B8C5CC;
  --color-border-soft:         #E3E6E6;

  /* Tipografía */
  --font-brand:                "Terpel Sans", sans-serif;
  --font-body:                 "Nunito", sans-serif;
  --font-system:               "Terpel Sans", "Nunito", system-ui, sans-serif;

  /* Tamaños */
  --text-micro: 10px; --text-xs: 12px; --text-sm: 14px;
  --text-md: 16px; --text-lg: 18px; --text-xl: 20px;
  --text-2xl: 24px; --text-3xl: 26px;

  /* Pesos */
  --weight-light: 300; --weight-regular: 400; --weight-medium: 500;
  --weight-semibold: 600; --weight-bold: 700; --weight-extrabold: 800; --weight-black: 900;

  /* Espaciado */
  --sp-1:4px; --sp-2:8px; --sp-3:12px; --sp-4:16px;
  --sp-5:20px; --sp-6:24px; --sp-8:32px; --sp-10:40px;

  /* Radio */
  --r-xs:4px; --r-sm:8px; --r-md:12px; --r-lg:16px;
  --r-xl:20px; --r-pill:40px;

  /* Sombras */
  --shadow-sm: 0 1px 3px rgba(0,19,51,0.08);
  --shadow-md: 0 2px 8px rgba(0,19,51,0.12);
  --shadow-lg: 0 4px 16px rgba(0,19,51,0.16);
  --shadow-xl: 0 8px 32px rgba(0,19,51,0.20);
}

/* Dark mode */
[data-theme="dark"] {
  --color-bg-page:      #12121E;
  --color-bg-surface:   #1C1C2E;
  --color-bg-alt:       #242438;
  --color-bg-nav:       #0A0A14;
  --color-text-primary: #C7C8CD;
  --color-text-heading: #E8EAF6;
  --color-text-secondary:#7D8F99;
  --color-border-default:#2A2A40;
  --color-border-input:  #3A3A58;
  --color-border-soft:   #242438;
}

* { box-sizing: border-box; margin: 0; padding: 0; }
@keyframes spin    { to { transform: rotate(360deg); } }
@keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.4} }
@keyframes fadeIn  { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:none} }
@keyframes slideInRight { from{transform:translateX(110%);opacity:0} to{transform:none;opacity:1} }
@keyframes scaleIn { from{transform:scale(.92);opacity:0} to{transform:scale(1);opacity:1} }
button:focus-visible,input:focus-visible,select:focus-visible,[tabindex="0"]:focus-visible {
  outline: 2px solid #DF1A09; outline-offset: 2px;
}
`;
